/**
 * `/ai-insights` — WP-M4, Task 57. `web/shapes/ai-insights.md` is the agreed shape.
 *
 * ── The order of this page is the argument ──────────────────────────────────
 *
 * The disclaimer is **first**, above every AI result, because spec 01 §16 makes it
 * non-hideable and a non-hideable sentence under eight hundred pixels of model prose
 * is one that gets read after the decision. Then the ask box, then the stored
 * insights, then the evidence panel, then the request form, then what the screen
 * cannot read. Nothing about that order is decoration; it is §2 of the shape file.
 *
 * ── Every filter is a server parameter ──────────────────────────────────────
 *
 * `insight_type`, `date_from` and `date_to` travel in the query string.
 * `GET /ai/insights` is **cursor**-paged and its envelope publishes `next_cursor` and
 * `has_more` and **no total**, so a browser-side filter would leave a count with
 * nothing to check it against — and the count in question is "how many analyses were
 * written about your people". Every number on this screen is therefore labelled
 * *what was read*.
 *
 * ── An empty page is about the filter, never about the budget ───────────────
 *
 * The three sentences kept apart on this screen are *"nothing was found"*, *"the model
 * could not be reached"* and *"we chose not to spend"*. The first is this
 * `EmptyState`, the second is a `fallback` row's own card, and the third is either a
 * `budgetRefused` card (queued path) or `RefusalPanel`'s budget branch (synchronous
 * path). They share no component and no words.
 *
 * ── The evidence panel is a sensitive read, so it opens on demand ───────────
 *
 * It is not mounted until a card asks for it, and it issues no request until a usable
 * access reason exists. A screen that read it eagerly would write a
 * `wp_data_access_logs` row every time somebody scrolled past.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { DateField } from '../components/DateField';
import { aiInsightsPath } from '../api/paths';
import type { AiInsightListResponse, AiInsightTypeName } from '../api/types';
import { useApiQuery } from '../api/useApiQuery';
import { readAiRefusal } from '../ai/refusal';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AbsentSurfaces } from './ai/AbsentSurfaces';
import { AiDisclaimer } from './ai/AiDisclaimer';
import { AnalyzeForm } from './ai/AnalyzeForm';
import { AskPanel } from './ai/AskPanel';
import { EvidencePanel } from './ai/EvidencePanel';
import { InsightCard } from './ai/InsightCard';
import { RefusalPanel } from './ai/RefusalPanel';

/** One page. The endpoint's own maximum is higher; this is what one screen reads. */
const PAGE_LIMIT = 20;

const FILTER_TYPES: readonly AiInsightTypeName[] = [
  'summary',
  'trend_comparison',
  'anomaly',
  'cause_hypothesis',
  'recommendation',
  'workload_analysis',
  'scoped_query',
];

const TYPE_KEY = {
  summary: 'm4.type.summary',
  trend_comparison: 'm4.type.trend_comparison',
  anomaly: 'm4.type.anomaly',
  cause_hypothesis: 'm4.type.cause_hypothesis',
  recommendation: 'm4.type.recommendation',
  workload_analysis: 'm4.type.workload_analysis',
  scoped_query: 'm4.type.scoped_query',
} as const;

export interface AiInsightsScreenProps {
  /** Injected so no assertion in this screen's tests is a wall-clock assertion. */
  readonly today?: string;
}

function isoDaysBefore(today: string, days: number): string {
  const base = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(base)) return today;
  return new Date(base - days * 86_400_000).toISOString().slice(0, 10);
}

export function AiInsightsScreen({ today = '2026-08-26' }: AiInsightsScreenProps = {}): ReactNode {
  const { t } = useI18n();
  const [type, setType] = useState<'' | AiInsightTypeName>('');
  const [from, setFrom] = useState(isoDaysBefore(today, 29));
  const [to, setTo] = useState(today);
  const [applied, setApplied] = useState({
    type: '' as '' | AiInsightTypeName,
    from: isoDaysBefore(today, 29),
    to: today,
  });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [openEvidenceFor, setOpenEvidenceFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const path = useMemo(
    () =>
      aiInsightsPath({
        limit: PAGE_LIMIT,
        insightType: applied.type === '' ? undefined : applied.type,
        dateFrom: applied.from,
        dateTo: applied.to,
        cursor,
      }),
    [applied, cursor],
  );

  const query = useApiQuery<AiInsightListResponse>(path);
  const rows = query.data?.data ?? [];
  const page = query.data?.page;

  const onOpenEvidence = useCallback((insightId: string) => {
    setOpenEvidenceFor(insightId);
  }, []);

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.aiInsights')}</ScreenHeading>

      {/* First, above everything a model wrote. Spec 01 §16. */}
      <AiDisclaimer />

      <p className="wp-small wp-muted" data-testid="m4-intro">
        {t('m4.intro')}
      </p>

      <AskPanel onOpenEvidence={onOpenEvidence} periodStart={applied.from} periodEnd={applied.to} />

      <section className="wp-section" aria-labelledby="m4-list-heading" data-testid="m4-list">
        <h2 className="wp-section__title" id="m4-list-heading" tabIndex={-1}>
          {t('m4.list.title')}
        </h2>
        <p className="wp-small wp-muted" data-testid="m4-list-caption">
          {t('m4.list.caption')}
        </p>

        <div className="wp-filters">
          <div className="wp-field">
            <label className="wp-field__label" htmlFor="m4-filter-type">
              {t('m4.filter.type')}
            </label>
            <select
              className="wp-field__input"
              id="m4-filter-type"
              data-testid="m4-filter-type"
              value={type}
              onChange={(event) => {
                setType(event.target.value as '' | AiInsightTypeName);
              }}
            >
              <option value="">{t('m4.filter.type.all')}</option>
              {FILTER_TYPES.map((entry) => (
                <option key={entry} value={entry}>
                  {t(TYPE_KEY[entry])}
                </option>
              ))}
            </select>
          </div>
          <DateField
            id="m4-filter-from"
            testId="m4-filter-from"
            label={t('m4.filter.from')}
            value={from}
            onChange={setFrom}
          />
          <DateField
            id="m4-filter-to"
            testId="m4-filter-to"
            label={t('m4.filter.to')}
            value={to}
            onChange={setTo}
          />
          <div className="wp-filters__actions">
            <button
              type="button"
              className="wp-button"
              data-testid="m4-filter-apply"
              onClick={() => {
                // The cursor belongs to the previous filter's ordering, so it is
                // dropped rather than carried across: forwarding it would page into
                // a list the operator is no longer looking at.
                setCursor(undefined);
                setApplied({ type, from, to });
              }}
            >
              {t('m4.filter.apply')}
            </button>
          </div>
        </div>
        <p className="wp-small wp-muted" data-testid="m4-filter-server-side">
          {t('m4.filter.serverSide')}
        </p>

        {query.status === 'loading' ? (
          <Skeleton rows={3} label={t('m4.subject.insights')} testId="m4-list-skeleton" />
        ) : query.status === 'error' && query.error !== null ? (
          <RefusalPanel
            refusal={readAiRefusal(query.error)}
            error={query.error}
            subject={t('m4.subject.insights')}
            onRetry={query.reload}
          />
        ) : rows.length === 0 ? (
          // About the **filter**. Never about the company, and never about the
          // budget — that has its own panel and its own words.
          <EmptyState
            title={t('m4.list.empty.title')}
            description={t('m4.list.empty.body')}
            testId="m4-list-empty"
          />
        ) : (
          <>
            <p className="wp-small" data-testid="m4-list-read-count">
              {t('m4.list.readCount', { count: rows.length })}
            </p>
            <p className="wp-small wp-muted" data-testid="m4-list-no-total">
              {t('m4.list.noTotal')}
            </p>
            {page?.has_more === true ? (
              <PartialData
                excludes={t('m4.list.readCount', { count: rows.length })}
                testId="m4-list-partial"
              />
            ) : null}
            <div className="wp-ai-cards">
              {rows.map((row) => (
                <InsightCard
                  key={row.id}
                  row={row as unknown as Readonly<Record<string, unknown>>}
                  headingId={`m4-insight-heading-${row.id}`}
                  onOpenEvidence={onOpenEvidence}
                />
              ))}
            </div>
            {page?.next_cursor === null || page?.next_cursor === undefined ? null : (
              <button
                type="button"
                className="wp-button"
                data-testid="m4-list-more"
                onClick={() => {
                  setCursor(page.next_cursor ?? undefined);
                }}
              >
                {t('m4.list.more')}
              </button>
            )}
          </>
        )}
      </section>

      {openEvidenceFor === null ? null : (
        <EvidencePanel insightId={openEvidenceFor} reason={reason} onReason={setReason} />
      )}

      <AnalyzeForm
        defaultFrom={applied.from}
        defaultTo={applied.to}
        onOpenEvidence={onOpenEvidence}
      />

      <AbsentSurfaces />
    </div>
  );
}
